<template>
  <div class="calendar-tree">
    <div class="tree-header">
      <h5 class="mb-3">
        <i class="bi bi-calendar3"></i>
        Календарь
      </h5>
    </div>
    
    <div v-if="isLoading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-muted" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
      <p class="text-muted mt-2 small">Построение календаря...</p>
    </div>
    
    <div v-else-if="Object.keys(calendarTree).length === 0" class="text-center py-4">
      <i class="bi bi-calendar-x display-4 text-muted"></i>
      <p class="text-muted mt-2">Нет материалов для отображения</p>
    </div>
    
    <div v-else class="tree-content">
      <div 
        v-for="(yearData, year) in calendarTree" 
        :key="year" 
        class="year-node"
        :class="{ 'expanded': expandedYears.has(parseInt(year)) }"
      >
        <!-- Узел года -->
        <div 
          class="year-header" 
          @click="toggleYear(parseInt(year))"
          :class="{ 'active': selectedYear === parseInt(year) }"
        >
          <i 
            class="bi expand-icon"
            :class="expandedYears.has(parseInt(year)) ? 'bi-chevron-down' : 'bi-chevron-right'"
          ></i>
          <i class="bi bi-calendar-event me-2"></i>
          <span class="year-text">{{ year }}</span>
          <span class="count-badge">{{ yearData.totalMaterials }}</span>
        </div>
        
        <!-- Месяцы (показываются при развернутом годе) -->
        <div v-if="expandedYears.has(parseInt(year))" class="months-container">
          <div 
            v-for="(monthData, monthNum) in yearData.months" 
            :key="monthNum"
            class="month-node"
            :class="{ 'expanded': expandedMonths.has(`${year}-${monthNum}`) }"
          >
            <!-- Узел месяца -->
            <div 
              class="month-header"
              @click="toggleMonth(parseInt(year), parseInt(monthNum))"
              :class="{ 'active': selectedMonth === `${year}-${monthNum}` }"
            >
              <i 
                class="bi expand-icon"
                :class="expandedMonths.has(`${year}-${monthNum}`) ? 'bi-chevron-down' : 'bi-chevron-right'"
              ></i>
              <i class="bi bi-folder me-2"></i>
              <span class="month-text">{{ getMonthName(parseInt(monthNum)) }}</span>
              <span class="count-badge">{{ monthData.totalMaterials }}</span>
            </div>
            
            <!-- Плейлисты (показываются при развернутом месяце) -->
            <div v-if="expandedMonths.has(`${year}-${monthNum}`)" class="playlists-container">
              <div 
                v-for="playlist in monthData.playlists" 
                :key="playlist.id"
                class="playlist-node"
                @click="selectPlaylist(playlist)"
                :class="{ 
                  'active': selectedPlaylist?.id === playlist.id,
                  'has-materials': playlist.materialsCount > 0 
                }"
              >
                <i class="bi bi-collection-play me-2"></i>
                <span class="playlist-title" :title="playlist.caption">
                  {{ playlist.caption }}
                </span>
                <span class="count-badge">{{ playlist.materialsCount }}</span>
              </div>
              
              <!-- Показать отдельные материалы без плейлиста -->
              <div 
                v-if="monthData.looseMaterials.length > 0"
                class="loose-materials-node"
                @click="selectLooseMaterials(parseInt(year), parseInt(monthNum))"
                :class="{ 'active': selectedLooseMaterials === `${year}-${monthNum}` }"
              >
                <i class="bi bi-file-earmark-play me-2"></i>
                <span class="loose-title">Отдельные материалы</span>
                <span class="count-badge">{{ monthData.looseMaterials.length }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'

export default {
  name: 'CalendarTreeComponent',
  
  props: {
    materials: {
      type: Array,
      default: () => []
    },
    playlists: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  
  emits: [
    'playlist-selected',
    'loose-materials-selected',
    'year-selected',
    'month-selected'
  ],
  
  setup(props, { emit }) {
    // Состояние компонента
    const expandedYears = ref(new Set())
    const expandedMonths = ref(new Set())
    const selectedYear = ref(null)
    const selectedMonth = ref(null)
    const selectedPlaylist = ref(null)
    const selectedLooseMaterials = ref(null)
    
    // Названия месяцев
    const monthNames = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ]
    
    const getMonthName = (monthNum) => {
      return monthNames[monthNum - 1] || `Месяц ${monthNum}`
    }
    
    // Построение календарного дерева
    const calendarTree = computed(() => {
      if (!props.materials.length) return {}
      
      const tree = {}
      
      // Группируем материалы по датам
      props.materials.forEach(material => {
        const date = getMediaDate(material)
        if (!date) return
        
        const year = date.getFullYear()
        const month = date.getMonth() + 1
        
        // Инициализируем структуру года
        if (!tree[year]) {
          tree[year] = {
            totalMaterials: 0,
            months: {}
          }
        }
        
        // Инициализируем структуру месяца
        if (!tree[year].months[month]) {
          tree[year].months[month] = {
            totalMaterials: 0,
            playlists: [],
            looseMaterials: []
          }
        }
        
        // Добавляем материал
        tree[year].totalMaterials++
        tree[year].months[month].totalMaterials++
        
        // Определяем, к какому плейлисту относится материал
        if (material.collection_id) {
          // Найдем или создадим плейлист в этом месяце
          let playlist = tree[year].months[month].playlists.find(p => p.id === material.collection_id)
          if (!playlist) {
            const playlistInfo = props.playlists.find(p => p.id === material.collection_id)
            playlist = {
              id: material.collection_id,
              caption: playlistInfo?.caption || `Плейлист ${material.collection_id}`,
              materialsCount: 0
            }
            tree[year].months[month].playlists.push(playlist)
          }
          playlist.materialsCount++
        } else {
          // Материал без плейлиста
          tree[year].months[month].looseMaterials.push(material)
        }
      })
      
      // Сортируем годы и месяцы
      const sortedTree = {}
      Object.keys(tree)
        .map(year => parseInt(year))
        .sort((a, b) => b - a) // Сначала новые годы
        .forEach(year => {
          sortedTree[year] = {
            ...tree[year],
            months: {}
          }
          
          Object.keys(tree[year].months)
            .map(month => parseInt(month))
            .sort((a, b) => b - a) // Сначала новые месяцы
            .forEach(month => {
              sortedTree[year].months[month] = tree[year].months[month]
              
              // Сортируем плейлисты по названию
              sortedTree[year].months[month].playlists.sort((a, b) => 
                a.caption.localeCompare(b.caption)
              )
            })
        })
      
      return sortedTree
    })
    
    // Получение даты материала с приоритетами
    const getMediaDate = (material) => {
      // 1. original_recording_date (приоритет 1)
      if (material.original_recording_date) {
        const date = new Date(material.original_recording_date)
        if (!isNaN(date.getTime())) return date
      }
      
      // 2. upload_date_youtube (приоритет 2) 
      if (material.upload_date_youtube) {
        const date = new Date(material.upload_date_youtube)
        if (!isNaN(date.getTime())) return date
      }
      
      // 3. created_at (приоритет 3)
      if (material.created_at) {
        const date = new Date(material.created_at)
        if (!isNaN(date.getTime())) return date
      }
      
      return null
    }
    
    
    // Управление разворачиванием узлов
    const toggleYear = (year) => {
      if (expandedYears.value.has(year)) {
        expandedYears.value.delete(year)
        // Сворачиваем все месяцы этого года
        Array.from(expandedMonths.value).forEach(monthKey => {
          if (monthKey.startsWith(`${year}-`)) {
            expandedMonths.value.delete(monthKey)
          }
        })
      } else {
        expandedYears.value.add(year)
      }
      selectedYear.value = year
      emit('year-selected', year)
    }
    
    const toggleMonth = (year, month) => {
      const monthKey = `${year}-${month}`
      if (expandedMonths.value.has(monthKey)) {
        expandedMonths.value.delete(monthKey)
      } else {
        expandedMonths.value.add(monthKey)
      }
      selectedMonth.value = monthKey
      emit('month-selected', { year, month })
    }
    
    const selectPlaylist = (playlist) => {
      selectedPlaylist.value = playlist
      selectedLooseMaterials.value = null
      emit('playlist-selected', playlist)
    }
    
    const selectLooseMaterials = (year, month) => {
      const monthKey = `${year}-${month}`
      selectedLooseMaterials.value = monthKey
      selectedPlaylist.value = null
      
      // Получаем отдельные материалы для этого месяца
      const looseMaterials = calendarTree.value[year]?.months[month]?.looseMaterials || []
      emit('loose-materials-selected', { year, month, materials: looseMaterials })
    }
    
    // Автоматическое разворачивание самого нового года при загрузке
    watch(() => calendarTree.value, (newTree) => {
      if (Object.keys(newTree).length > 0) {
        const latestYear = Math.max(...Object.keys(newTree).map(y => parseInt(y)))
        expandedYears.value.add(latestYear)
      }
    }, { immediate: true })
    
    return {
      expandedYears,
      expandedMonths,
      selectedYear,
      selectedMonth,
      selectedPlaylist,
      selectedLooseMaterials,
      calendarTree,
      getMonthName,
      toggleYear,
      toggleMonth,
      selectPlaylist,
      selectLooseMaterials
    }
  }
}
</script>

<style scoped>
.calendar-tree {
  background: var(--yt-bg-secondary);
  border-radius: 8px;
  padding: 1rem;
  height: fit-content;
  max-height: 80vh;
  overflow-y: auto;
}

.tree-header h5 {
  color: var(--yt-text-secondary);
  font-weight: 600;
}

.tree-content {
  font-size: 0.9rem;
}

/* Узел года */
.year-node {
  margin-bottom: 0.5rem;
}

.year-header {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background: var(--yt-bg-primary);
  border: 1px solid var(--yt-border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.25rem;
}

.year-header:hover {
  background: var(--yt-bg-tertiary);
  border-color: var(--yt-border-color);
}

.year-header.active {
  background: var(--yt-primary);
  color: var(--yt-bg-primary);
  border-color: var(--yt-primary);
}

.expand-icon {
  width: 16px;
  margin-right: 0.5rem;
}

.year-text {
  flex: 1;
  font-weight: 600;
}

.count-badge {
  background: var(--yt-text-secondary);
  color: var(--yt-bg-primary);
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.year-header.active .count-badge {
  background: rgba(255, 255, 255, 0.3);
}

/* Контейнер месяцев */
.months-container {
  margin-left: 1rem;
  border-left: 2px solid var(--yt-border-color);
  padding-left: 0.5rem;
}

/* Узел месяца */
.month-node {
  margin-bottom: 0.25rem;
}

.month-header {
  display: flex;
  align-items: center;
  padding: 0.4rem;
  background: var(--yt-bg-primary);
  border: 1px solid var(--yt-bg-tertiary);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.25rem;
}

.month-header:hover {
  background: var(--yt-bg-secondary);
  border-color: var(--yt-border-color);
}

.month-header.active {
  background: var(--yt-success);
  color: var(--yt-bg-primary);
  border-color: var(--yt-success);
}

.month-text {
  flex: 1;
  font-weight: 500;
}

.month-header.active .count-badge {
  background: rgba(255, 255, 255, 0.3);
}

/* Контейнер плейлистов */
.playlists-container {
  margin-left: 1rem;
  border-left: 2px solid var(--yt-bg-tertiary);
  padding-left: 0.5rem;
}

/* Узел плейлиста */
.playlist-node, .loose-materials-node {
  display: flex;
  align-items: center;
  padding: 0.3rem 0.5rem;
  background: var(--yt-bg-primary);
  border: 1px solid var(--yt-bg-secondary);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.2rem;
}

.playlist-node:hover, .loose-materials-node:hover {
  background: var(--yt-bg-secondary);
  border-color: var(--yt-bg-tertiary);
}

.playlist-node.active, .loose-materials-node.active {
  background: var(--yt-warning);
  color: var(--yt-text-primary);
  border-color: var(--yt-warning);
}

.playlist-title, .loose-title {
  flex: 1;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.playlist-node.active .count-badge, .loose-materials-node.active .count-badge {
  background: rgba(0, 0, 0, 0.2);
  color: var(--yt-text-primary);
}

/* Отдельные материалы */
.loose-materials-node {
  font-style: italic;
  opacity: 0.8;
}

.loose-materials-node .bi-file-earmark-play {
  color: var(--yt-text-secondary);
}

/* Скроллбар */
.calendar-tree::-webkit-scrollbar {
  width: 6px;
}

.calendar-tree::-webkit-scrollbar-track {
  background: var(--yt-bg-secondary);
  border-radius: 3px;
}

.calendar-tree::-webkit-scrollbar-thumb {
  background: var(--yt-text-muted);
  border-radius: 3px;
}

.calendar-tree::-webkit-scrollbar-thumb:hover {
  background: var(--yt-text-secondary);
}

/* Адаптивность */
@media (max-width: 768px) {
  .calendar-tree {
    padding: 0.75rem;
    max-height: 60vh;
  }

  .year-header, .month-header {
    padding: 0.4rem;
  }

  .playlist-node, .loose-materials-node {
    padding: 0.25rem 0.4rem;
  }
}
</style>